import type { BenchmarkSex, ModificationKind } from "src/models";

/** Console mirror of the grading-service session contracts (specs/001 contracts). */
export type SessionStatus = "planned" | "open" | "review" | "finalized";
export type SessionWindow = "fall" | "spring" | "custom";
export type Category = "core" | "cardio" | "upper";
export type EventId =
  | "pft-situps"
  | "pft-plank"
  | "pft-mile"
  | "pft-beep-20m"
  | "pft-beep-15m"
  | "pft-pushups"
  | "pft-pullups";
export type EntryUnit = "reps" | "seconds" | "laps";
export type EntryMode = "run" | "walk" | "wheel" | "pull-up" | "chin-up";
export type StudentStatus = "present" | "absent" | "excused" | "makeup_pending";
export type AwardTier =
  | "presidential"
  | "national"
  | "participation"
  | "incomplete";
export type SessionAction =
  | "open"
  | "close_stations"
  | "reopen_stations"
  | "finalize"
  | "reopen";

export const EVENT_UNIT: Record<EventId, EntryUnit> = {
  "pft-situps": "reps",
  "pft-plank": "seconds",
  "pft-mile": "seconds",
  "pft-beep-20m": "laps",
  "pft-beep-15m": "laps",
  "pft-pushups": "reps",
  "pft-pullups": "reps",
};

export const EVENT_LABEL: Record<EventId, string> = {
  "pft-situps": "Sit-Ups",
  "pft-plank": "Plank",
  "pft-mile": "1-Mile Run",
  "pft-beep-20m": "20-Meter Beep Test",
  "pft-beep-15m": "15-Meter Beep Test",
  "pft-pushups": "Right-Angle Push-Ups",
  "pft-pullups": "Pull-Ups",
};

export const CATEGORY_EVENTS: Record<Category, EventId[]> = {
  core: ["pft-situps", "pft-plank"],
  cardio: ["pft-mile", "pft-beep-20m"],
  upper: ["pft-pushups", "pft-pullups"],
};

export interface RosterStudent {
  studentId: string;
  firstName: string;
  lastName: string;
  lastInitial: string;
  benchmarkSex: BenchmarkSex | null;
  benchmarkSexSource: "roster" | "school";
  ageAtTest: number | null;
  gradeLevel?: string | null;
  plannedModification: ModificationKind;
  plannedEventId: string | null;
  plannedNote: string | null;
  healthFlags?: string[];
  eventChoices?: Partial<Record<Category, string>>;
}

export interface Station {
  id: string;
  category: Category;
  eventId: EventId | string;
  label: string;
  order: number;
  locked: boolean;
  groupAssignments: Record<string, number>;
}

export interface EntryDto {
  id: string;
  stationId?: string;
  studentId: string;
  eventId: EventId | string;
  attempt?: number;
  value: number;
  unit: EntryUnit;
  mode?: EntryMode;
  modification: ModificationKind;
  modificationNote?: string;
  warningGiven?: boolean;
  lapCount?: number;
  advisoryCount?: number;
  recordedAt: string;
}

export interface SessionEntry extends EntryDto {
  category?: Category;
  supersededBy?: string | null;
  lockedByTeacher?: boolean;
  recordedByType?: string;
  /** Linked self-test result when the entry came from a make-up upload. */
  testResultId?: string | null;
  receivedAt?: string;
}

export interface EntryResponse {
  id: string;
  outcome:
    | "stored"
    | "superseded_existing"
    | "superseded_incoming"
    | "rejected";
  conflictFlagId: string | null;
  categoryResult: CategoryResult | null;
  error: string | null;
}

export interface CategoryResult {
  category: Category;
  eventId: string;
  entryId?: string;
  value: number;
  mode?: string;
  modification: ModificationKind;
  excluded: boolean;
  presidentialMet: boolean;
  nationalMet: boolean;
  presidentialThreshold: number;
  nationalThreshold: number;
  nextTierGap: number | null;
  alternateThresholds?: {
    sex: BenchmarkSex;
    presidentialThreshold: number;
    nationalThreshold: number;
  };
}

export interface SessionDetail {
  id: string;
  classId: string | null;
  schoolId: string | null;
  districtId: string | null;
  teacherId: string;
  name: string;
  date: string;
  window: SessionWindow;
  status: SessionStatus;
  benchmarkVersion: number | null;
  eventsByCategory: Record<Category, EventId | "student-choice"> | null;
  cardioLast: boolean;
  rosterSnapshot: RosterStudent[];
  joinCode: string | null;
  joinCodeExpiresAt: string | null;
  finalizedAt: string | null;
  stations: Station[];
  entries: SessionEntry[];
  studentStatuses: { studentId: string; status: StudentStatus }[];
  openFlagCount: number;
}

export interface SessionSummary {
  presidential: number;
  national: number;
  participation: number;
  incomplete: number;
  absent: number;
  excused: number;
}

export interface StudentResult {
  studentId: string;
  firstName: string;
  lastName: string;
  ageAtTest: number | null;
  benchmarkSex: BenchmarkSex | null;
  benchmarkSexSource?: "roster" | "school";
  benchmarkVersion: number | null;
  awardTier: AwardTier;
  categories: Partial<Record<Category, CategoryResult>>;
  status: StudentStatus;
  missingCategories: Category[];
}

export type FlagType =
  | "missing_category"
  | "mod_without_note"
  | "missing_mode"
  | "outlier"
  | "duplicate"
  | "camera_disagreement"
  | "conflict"
  | "age_below_range"
  | "superseded_certificate"
  | "join_abuse";

export interface ReviewFlag {
  id: string;
  sessionId: string;
  type: FlagType;
  status: "open" | "resolved" | "dismissed";
  studentId: string | null;
  entryId: string | null;
  details: Record<string, any>;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

export interface PriorWindow {
  sessionId: string;
  date: string;
  window: SessionWindow;
  awardTier: AwardTier;
  categories: Partial<Record<Category, CategoryResult>>;
}

export interface ReviewView {
  session: SessionDetail;
  students: StudentResult[];
  flags: ReviewFlag[];
  summary: SessionSummary;
}

export interface CreateSessionRequest {
  classId: string;
  name?: string;
  date: string;
  window: SessionWindow;
  eventsByCategory: Record<Category, EventId | "student-choice">;
  cardioLast?: boolean;
  makeUp?: boolean;
}

export interface BenchmarkRow {
  sex: BenchmarkSex;
  age: number;
  eventId: string;
  presidentialThreshold: number;
  nationalThreshold: number;
  comparison: "min" | "max";
  unit: EntryUnit;
}

/**
 * Whether a session's family letters can be emailed (`GET v1/sessions/:id/documents`, research
 * R12): only when the district delivers by email and this deployment can send email.
 */
export interface LetterEmailStatus {
  available: boolean;
  reason: "print-only" | "not-configured" | null;
}
